import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Small Stripe-style status pill — a tinted rounded badge with a
 * leading dot. Shared dashboard primitive (apps/web CLAUDE.md §13):
 * green for live/active, amber for in-progress/attention, neutral for
 * inactive. Extend `StatusTone` (and the maps below) when a new state
 * needs its own colour.
 */
export type StatusTone = 'success' | 'warning' | 'muted';

interface StatusPillProps {
  readonly tone: StatusTone;
  readonly children: ReactNode;
}

const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  muted: 'bg-muted text-muted-foreground',
};

const DOT_CLASSES: Record<StatusTone, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  muted: 'bg-muted-foreground',
};

export function StatusPill({ tone, children }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        TONE_CLASSES[tone],
      )}
    >
      <span aria-hidden="true" className={cn('size-1.5 rounded-full', DOT_CLASSES[tone])} />
      {children}
    </span>
  );
}
